import { useCallback, useEffect, useState } from 'react';
import { getApiErrorMessage } from '../api/client';
import AddParticipantModal from '../components/AddParticipantModal';
import {
  cancelCompetitionApplication,
  fetchTournamentParticipants,
  fetchTournaments,
} from '../api/competitionApplication';
import { useI18n } from '../i18n';
import { formatEventDate, normalizeCompetitionList } from '../utils/competitionList';
import {
  formatAmount,
  formatAppliedAt,
  formatGender,
  formatGrade,
  formatHeight,
  formatPaymentStatus,
  formatStatus,
  normalizeParticipantList,
} from '../utils/participantList';
import { exportParticipantsToExcel } from '../utils/exportParticipantsExcel';

// 오류 상태는 { key, error } 로 두고 화면에 그릴 때 현재 언어로 만든다
function errorText(state, t) {
  return state ? getApiErrorMessage(state.error, t(state.key)) : null;
}

function ParticipantListPage() {
  const { t } = useI18n();
  const [tournaments, setTournaments] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  const [tournamentsError, setTournamentsError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const loadTournaments = useCallback(async () => {
    setTournamentsLoading(true);
    setTournamentsError(null);
    try {
      const data = await fetchTournaments();
      const list = normalizeCompetitionList(data);
      if (list.length === 0) {
        setTournamentsError({ key: 'participants.noTournaments' });
      }
      setTournaments(list);
    } catch (error) {
      setTournamentsError({ key: 'participants.loadTournamentsError', error });
      setTournaments([]);
    } finally {
      setTournamentsLoading(false);
    }
  }, []);

  const loadParticipants = useCallback(async (tournamentId) => {
    setParticipantsLoading(true);
    setParticipantsError(null);
    setParticipants([]);
    try {
      const data = await fetchTournamentParticipants(tournamentId);
      const list = normalizeParticipantList(data);
      if (list.length === 0) {
        setParticipantsError({ key: 'participants.noParticipants' });
      }
      setParticipants(list);
    } catch (error) {
      setParticipantsError({ key: 'participants.loadParticipantsError', error });
      setParticipants([]);
    } finally {
      setParticipantsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  useEffect(() => {
    if (selectedId == null) return;
    loadParticipants(selectedId);
  }, [selectedId, loadParticipants]);

  const selectedTournament = tournaments.find((item) => item.id === selectedId);

  const handleExcelDownload = () => {
    if (participants.length === 0) return;
    exportParticipantsToExcel(participants, selectedTournament?.name);
  };

  const handleCancelApplication = async (participant) => {
    const confirmed = window.confirm(
      t('participants.cancelConfirm', { name: participant.participantName })
    );
    if (!confirmed) return;

    setCancellingId(participant.id);
    try {
      await cancelCompetitionApplication(participant.id);
      const next = participants.filter((p) => p.id !== participant.id);
      setParticipants(next);
      if (next.length === 0) {
        setParticipantsError({ key: 'participants.noParticipants' });
      }
    } catch (error) {
      alert(getApiErrorMessage(error, t('participants.cancelError')));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="container container-wide">
      <div className="page-intro">
        <p className="page-eyebrow">{t('participants.eyebrow')}</p>
        <h2 className="page-title">{t('participants.title')}</h2>
        <p className="page-desc">{t('participants.desc')}</p>
      </div>

      <div className="tournament-layout participant-list-layout">
        <section className="card tournament-select-panel">
          <h3 className="panel-title">{t('participants.selectTournament')}</h3>
          <div className="tournament-options">
            {tournamentsLoading && (
              <div className="list-status">
                <div className="skeleton-card" />
                <div className="skeleton-card" />
              </div>
            )}
            {!tournamentsLoading && tournamentsError && (
              <div className="list-status list-status-error">
                <p>{errorText(tournamentsError, t)}</p>
                <button type="button" className="btn btn-retry" onClick={loadTournaments}>
                  {t('common.retry')}
                </button>
              </div>
            )}
            {!tournamentsLoading &&
              !tournamentsError &&
              tournaments.map((tournament) => (
                <button
                  key={tournament.id}
                  type="button"
                  className={`tournament-card tournament-card-btn${
                    selectedId === tournament.id ? ' selected' : ''
                  }`}
                  onClick={() => setSelectedId(tournament.id)}
                >
                  <div className="tournament-card-inner">
                    <div className="tournament-info">
                      <div className="tournament-name">{tournament.name}</div>
                      <div className="tournament-meta">
                        {tournament.location && (
                          <span className="meta-chip">{tournament.location}</span>
                        )}
                        {tournament.eventAt && (
                          <span className="meta-chip">{formatEventDate(tournament.eventAt)}</span>
                        )}
                      </div>
                    </div>
                    <div className="tournament-check" aria-hidden="true" />
                  </div>
                </button>
              ))}
          </div>
        </section>

        <section className="card participants-panel">
          <div className="panel-header">
            <div>
              <h3 className="panel-title">{t('participants.listTitle')}</h3>
              {selectedTournament && (
                <p className="panel-subtitle">{selectedTournament.name}</p>
              )}
            </div>
            <div className="panel-header-actions">
              {selectedId != null && (
                <>
                  <button
                    type="button"
                    className="btn btn-add-participant"
                    onClick={() => setAddModalOpen(true)}
                  >
                    {t('participants.addParticipant')}
                  </button>
                  {participants.length > 0 && (
                    <>
                      <button
                        type="button"
                        className="btn btn-download"
                        onClick={handleExcelDownload}
                      >
                        {t('participants.downloadExcel')}
                      </button>
                      <span className="participant-count">{t('common.people', { count: participants.length })}</span>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {selectedId == null && (
            <p className="panel-empty">{t('participants.selectTournamentPrompt')}</p>
          )}

          {selectedId != null && participantsLoading && (
            <div className="list-status">
              <div className="skeleton-card" />
              <div className="skeleton-card" />
              <div className="skeleton-card" />
            </div>
          )}

          {selectedId != null && !participantsLoading && participantsError && (
            <div className="list-status list-status-error">
              <p>{errorText(participantsError, t)}</p>
              <button
                type="button"
                className="btn btn-retry"
                onClick={() => loadParticipants(selectedId)}
              >
                {t('common.retry')}
              </button>
            </div>
          )}

          {selectedId != null &&
            !participantsLoading &&
            !participantsError &&
            participants.length > 0 && (
              <div className="participants-table-wrap">
                <table className="participants-table">
                  <thead>
                    <tr>
                      <th>{t('participants.columns.name')}</th>
                      <th>{t('participants.columns.association')}</th>
                      <th>{t('participants.columns.gym')}</th>
                      <th>{t('participants.columns.coach')}</th>
                      <th>{t('participants.columns.grade')}</th>
                      <th>{t('participants.columns.birth')}</th>
                      <th>{t('participants.columns.gender')}</th>
                      <th>{t('participants.columns.height')}</th>
                      <th>{t('participants.columns.phone')}</th>
                      <th>{t('participants.columns.parentPhone')}</th>
                      <th>{t('participants.columns.status')}</th>
                      <th>{t('participants.columns.payment')}</th>
                      <th>{t('participants.columns.amount')}</th>
                      <th>{t('participants.columns.appliedAt')}</th>
                      <th className="cell-actions">{t('participants.columns.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => (
                      <tr key={p.id}>
                        <td className="cell-name">{p.participantName}</td>
                        <td>{p.associationName || '-'}</td>
                        <td>{p.gymName ?? t('common.noGym')}</td>
                        <td>{p.coachName || '-'}</td>
                        <td>{formatGrade(p.grade) || '-'}</td>
                        <td>{p.birth || '-'}</td>
                        <td>
                          <span
                            className={`gender-badge${
                              p.gender === 'F' ? ' female' : p.gender === 'M' ? ' male' : ''
                            }`}
                          >
                            {formatGender(p.gender)}
                          </span>
                        </td>
                        <td>{formatHeight(p.height)}</td>
                        <td>{p.participantPhone || '-'}</td>
                        <td>{p.parentPhone || '-'}</td>
                        <td>{formatStatus(p.status)}</td>
                        <td>{formatPaymentStatus(p.paymentStatus)}</td>
                        <td>{formatAmount(p.amount)}</td>
                        <td>{formatAppliedAt(p.appliedAt)}</td>
                        <td className="cell-actions">
                          <button
                            type="button"
                            className="btn btn-cancel"
                            onClick={() => handleCancelApplication(p)}
                            disabled={cancellingId === p.id}
                          >
                            {cancellingId === p.id ? t('participants.processing') : t('participants.cancelApplication')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="participants-cards">
                  {participants.map((p) => (
                    <div key={p.id} className="participant-card">
                      <div className="participant-card-header">
                        <span className="participant-card-name">{p.participantName}</span>
                        <button
                          type="button"
                          className="btn btn-cancel"
                          onClick={() => handleCancelApplication(p)}
                          disabled={cancellingId === p.id}
                        >
                          {cancellingId === p.id ? t('participants.processing') : t('participants.cancelApplication')}
                        </button>
                      </div>
                      <dl className="participant-card-meta">
                        <div>
                          <dt>{t('participants.columns.association')}</dt>
                          <dd>{p.associationName || '-'}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.gym')}</dt>
                          <dd>{p.gymName ?? t('common.noGym')}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.coach')}</dt>
                          <dd>{p.coachName || '-'}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.grade')}</dt>
                          <dd>{formatGrade(p.grade) || '-'}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.birth')}</dt>
                          <dd>{p.birth || '-'}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.gender')}</dt>
                          <dd>{formatGender(p.gender)}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.height')}</dt>
                          <dd>{formatHeight(p.height)}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.phone')}</dt>
                          <dd>{p.participantPhone || '-'}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.parentPhone')}</dt>
                          <dd>{p.parentPhone || '-'}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.payment')}</dt>
                          <dd>{formatPaymentStatus(p.paymentStatus)}</dd>
                        </div>
                        <div>
                          <dt>{t('participants.columns.appliedAt')}</dt>
                          <dd>{formatAppliedAt(p.appliedAt)}</dd>
                        </div>
                      </dl>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </section>
      </div>

      <AddParticipantModal
        open={addModalOpen}
        tournament={selectedTournament}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => loadParticipants(selectedId)}
      />
    </div>
  );
}

export default ParticipantListPage;
